import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CBadge, CCard, CCardBody, CCardGroup, CCol, CRow } from "@coreui/react";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import SubscriptionPlanForm from "../subscriptionPlanForm";
import { subscriptionPlan } from "@/constants/translation/subscriptionPlan.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import SubscriptionPlanValidationClass from "@/helpers/validations/subscriptionPlan";
import { masters } from "@/libs/routes";
import { useSubscriptionPlan, useUpdateSubscriptionPlan } from "@/hooks/useSubscriptionPlan";
import constants from "@/libs/constant";

// API responses pass every value through the app's shared encryption round-trip,
// which stringifies booleans (true -> "true", false -> "false"). Native JS truthiness
// on the raw value is wrong here since the string "false" is truthy.
const isActiveValue = (value) => value === true || value === constants.status.apiStatus.true;

const getUserTypeBadgeClass = (userType) => {
  switch (userType) {
    case "freelancer_user":
      return "primary";
    case "normal_user":
      return "secondary";
    default:
      return "secondary";
  }
};

/**
 * Component for editing an existing subscription plan.
 * @returns {JSX.Element} The rendered EditSubscriptionPlan component.
 */
const EditSubscriptionPlan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useTranslationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: response, isLoading: isDataLoading } = useSubscriptionPlan(id, undefined, () => {
    toast.error(t(messages.toast.subscriptionPlan.dataFetchFailed));
  });

  const { mutate: doUpdateSubscriptionPlan } = useUpdateSubscriptionPlan(
    (response) => {
      toast.success(response?.message || t(messages.toast.subscriptionPlan.updated));
      navigate(masters.subscriptionPlan.list);
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.subscriptionPlan.updateFailed));
      setIsSubmitting(false);
    }
  );

  const handleUpdateSubscriptionPlan = (values) => {
    setIsSubmitting(true);
    doUpdateSubscriptionPlan({
      uuid: id,
      name: values.name,
      user_type: values.user_type,
      duration: values.duration,
      ...(values.apple_product_id?.trim() ? { apple_product_id: values.apple_product_id.trim() } : {}),
      ...(values.google_product_id?.trim() ? { google_product_id: values.google_product_id.trim() } : {}),
    });
  };

  const subscriptionPlanData = response?.data;

  const initialValues = useMemo(
    () => ({
      name: subscriptionPlanData?.name || "",
      user_type: subscriptionPlanData?.userType || "",
      duration: subscriptionPlanData?.duration || "",
      apple_product_id: subscriptionPlanData?.appleProductId || "",
      google_product_id: subscriptionPlanData?.googleProductId || "",
    }),
    [subscriptionPlanData]
  );

  const validationSchema = useMemo(() => SubscriptionPlanValidationClass.subscriptionPlanValidation(t), [t]);

  if (isDataLoading) {
    return (
      <CCardGroup>
        <CCard className="p-4">
          <div className="text-center">
            <p>{t(subscriptionPlan.edit.main_heading)}...</p>
          </div>
        </CCard>
      </CCardGroup>
    );
  }

  const isActive = isActiveValue(subscriptionPlanData?.isActive);

  return (
    <>
      <CCard className="mb-4 profile-header-card">
        <CCardBody>
          <CRow className="align-items-center">
            <CCol>
              <h2 className="mb-2">{subscriptionPlanData?.name}</h2>
              <div className="d-flex flex-wrap gap-2">
                <CBadge color={getUserTypeBadgeClass(subscriptionPlanData?.userType)}>
                  {t(subscriptionPlan.user_type[subscriptionPlanData?.userType]) || subscriptionPlanData?.userType}
                </CBadge>
                <CBadge color={isActive ? "success" : "danger"}>
                  {isActive ? t(subscriptionPlan.list.status.active) : t(subscriptionPlan.list.status.inactive)}
                </CBadge>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CCardGroup>
        <CCard className="p-4">
          <h1 className="text-center">{t(subscriptionPlan.edit.main_heading)}</h1>
          <SubscriptionPlanForm
            initialValues={initialValues}
            onSubmit={handleUpdateSubscriptionPlan}
            isSubmitting={isSubmitting}
            validationSchema={validationSchema}
            isEdit
          />
        </CCard>
      </CCardGroup>
    </>
  );
};

EditSubscriptionPlan.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.subscriptionPlan?.list,
    pathname: masters.subscriptionPlan.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.subscriptionPlan?.edit,
    pathname: "",
    active: true,
  },
];

export default EditSubscriptionPlan;
